// Formatting helpers for operations metrics. Pure functions, trivially
// unit-testable. Kept dependency-free so tests don't pull React.

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US')

export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value as number)) return '—'
  return NUMBER_FORMATTER.format(value)
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value as number)) return '—'
  return `${(value as number).toFixed(1)}%`
}

export function formatMs(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value as number)) return '—'
  const n = value as number
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`
  return `${Math.round(n)}ms`
}

// Compact token counts: 2,264,100 -> "2.26M"
export function formatTokens(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value as number)) return '—'
  const n = value as number
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(Math.round(n))
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return iso
  const deltaSec = Math.round((Date.now() - then) / 1000)
  if (deltaSec < 0) return 'just now'
  if (deltaSec < 60) return `${deltaSec}s ago`
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`
  return `${Math.floor(deltaSec / 86400)}d ago`
}

// RPM capacity utilization: bucketUsed / (keys * rpmPerKey) within the
// rolling minute. Clamped to [0, 100+] so a saturated bucket can show 100%.
export function rpmUtilizationPercent(health: {
  nvidiaKeys: number
  nvidiaRpmPerKey: number
  nvidiaBucketUsed: number
} | null | undefined): number {
  if (!health || !health.nvidiaKeys || !health.nvidiaRpmPerKey) return 0
  const capacity = health.nvidiaKeys * health.nvidiaRpmPerKey
  if (capacity <= 0) return 0
  return Math.max(0, Math.round((health.nvidiaBucketUsed / capacity) * 100))
}
